import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import * as d3 from 'd3';

const baconColors = [
  '#F5F5F5', '#D4D4D4', '#B5B5B5', '#969696', '#777777', '#5C5C5C',
];

const colorScale = d3.scaleOrdinal(baconColors);

const PAPER = '#F6F1E4';
const INK = '#0E0E0E';
const MEAT = '#C4452D';
const LABEL_INK = '#1A1208';

const MIN_ARC = 84;
const TARGET_PER_RING = 5;
const SCREEN_NAME = 13;
const SCREEN_HUB = 20;
const SCREEN_ROLE = 10;

const measureCtx = typeof document !== 'undefined'
  ? document.createElement('canvas').getContext('2d')
  : null;

const vinylGeometry = (width, height) => {
  const spread = Math.min(width, height);
  const vinylR = Math.max(96, spread / 2 - 26);
  const labelR = vinylR * 0.185;
  const inner = labelR + 22;
  const outer = vinylR - 22;
  return { vinylR, labelR, inner, outer };
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const defaultScale = (width, height, vinylR) => {
  const spread = Math.min(width, height);
  return Math.max(0.35, (spread / 2) / ((vinylR || 1) + 18));
};

const memberDotSize = (vinylR) => Math.max(5.5, vinylR * 0.028);

const radiusForCount = (count, inner, outer) => {
  const desiredArc = count <= 2 ? 120 : count <= 4 ? 100 : count <= 6 ? 90 : 84;
  const radius = (Math.max(1, count) * desiredArc) / (2 * Math.PI);
  return clamp(radius, inner, outer);
};

const radiiForRings = (ringCount, inner, outer, memberCount) => {
  if (ringCount <= 1) {
    return [radiusForCount(memberCount, inner, outer)];
  }

  const usedOuter = inner + (outer - inner) * Math.min(1, 0.7 + 0.15 * (ringCount - 1));
  return Array.from({ length: ringCount }, (_, i) => {
    const t = i / Math.max(1, ringCount - 1);
    return inner + (usedOuter - inner) * (0.08 + 0.92 * t);
  });
};

const capsForRadii = (radii, minArc) => (
  radii.map(radius => Math.max(1, Math.floor((2 * Math.PI * radius) / minArc)))
);

const wrapText = (ctx, text, maxWidth, maxLines) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const lines = [];
  let line = '';

  for (let i = 0; i < words.length; i++) {
    const test = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = words[i];
      if (lines.length === maxLines - 1) {
        const rest = [line, ...words.slice(i + 1)].join(' ');
        if (ctx.measureText(rest).width > maxWidth) {
          let clipped = rest;
          while (clipped.length > 1 && ctx.measureText(`${clipped}…`).width > maxWidth) {
            clipped = clipped.slice(0, -1);
          }
          lines.push(`${clipped}…`);
        } else {
          lines.push(rest);
        }
        return lines;
      }
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
};

const wrapName = (ctx, text, maxWidth) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  if (words.length === 1) return wrapText(ctx, text, maxWidth, 2);

  const first = words[0];
  const rest = words.slice(1).join(' ');
  if (ctx.measureText(rest).width <= maxWidth) {
    return [first, rest];
  }

  let clipped = rest;
  while (clipped.length > 1 && ctx.measureText(`${clipped}…`).width > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return [first, `${clipped}…`];
};

const boxesOverlap = (a, b, pad) => !(
  a.x + a.w + pad < b.x ||
  b.x + b.w + pad < a.x ||
  a.y + a.h + pad < b.y ||
  b.y + b.h + pad < a.y
);

const pointInBox = (x, y, box) => (
  !!box
  && x >= box.x
  && x <= box.x + box.w
  && y >= box.y
  && y <= box.y + box.h
);

const applyPolar = (node) => {
  node.x = node.fx = Math.cos(node.__angle) * node.__radius;
  node.y = node.fy = Math.sin(node.__angle) * node.__radius;
};

const placeRing = (ring) => {
  const count = Math.max(1, ring.nodes.length);
  const offset = ring.offset || 0;
  ring.nodes.forEach((node, j) => {
    node.__angle = -Math.PI / 2 + offset + (j * 2 * Math.PI) / count;
    node.__radius = ring.radius;
    applyPolar(node);
  });
};

const packByCount = (members, inner, outer, minArc) => {
  const startK = Math.max(1, Math.ceil(members.length / TARGET_PER_RING));

  for (let k = startK; k <= members.length; k++) {
    const radii = radiiForRings(k, inner, outer, members.length);
    const caps = capsForRadii(radii, minArc);
    const total = caps.reduce((sum, cap) => sum + cap, 0);
    if (total < members.length) continue;

    const rings = [];
    let idx = 0;
    for (let i = 0; i < k && idx < members.length; i++) {
      const remainingRings = k - i;
      const remainingMembers = members.length - idx;
      const minForRest = remainingRings - 1;
      let take = Math.min(caps[i], remainingMembers - minForRest);
      take = Math.max(1, take);
      rings.push({
        nodes: members.slice(idx, idx + take),
        radius: radii[i],
        offset: i % 2 === 1 ? Math.PI / Math.max(take, 1) : 0,
      });
      idx += take;
    }
    if (idx < members.length && rings.length) {
      rings[rings.length - 1].nodes.push(...members.slice(idx));
    }
    return rings;
  }
  return [{ nodes: members, radius: outer, offset: 0 }];
};

const assignRings = (members, inner, outer, minArc) => {
  const originals = members.filter(m => m.isOriginal);
  const others = members.filter(m => !m.isOriginal);

  if (originals.length > 0 && others.length > 0) {
    const radii = radiiForRings(2, inner, outer, members.length);
    const caps = capsForRadii(radii, minArc);
    if (originals.length <= caps[0] && others.length <= caps[1]) {
      return [
        {
          nodes: originals,
          radius: radiusForCount(originals.length, inner, radii[0]),
          offset: 0,
        },
        {
          nodes: others,
          radius: radiusForCount(others.length, Math.min(outer, radii[0] + 28), outer),
          offset: Math.PI / Math.max(others.length, 1),
        },
      ];
    }
  }

  return packByCount(members, inner, outer, minArc);
};

const labelPlan = (ctx, node, nodeSize, isHub, globalScale, includeRoles) => {
  const fontSize = (isHub ? SCREEN_HUB : SCREEN_NAME) / globalScale;
  const roleFontSize = SCREEN_ROLE / globalScale;
  const maxWidth = (isHub ? 120 : 78) / globalScale;
  const angle = isHub ? Math.PI / 2 : (node.__angle ?? Math.atan2(node.y, node.x));
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const originX = isHub ? node.x : node.x + cos * (nodeSize + 6);
  const originY = isHub ? node.y : node.y + sin * (nodeSize + 6);
  const stackDir = isHub || sin >= -0.35 ? 1 : -1;
  const textAlign = isHub || Math.abs(cos) < 0.35 ? 'center' : (cos > 0 ? 'left' : 'right');

  ctx.font = `700 ${fontSize}px 'Karla Variable', Karla, sans-serif`;
  const nameLines = wrapName(ctx, node.name, maxWidth);
  ctx.font = `500 ${roleFontSize}px 'Karla Variable', Karla, sans-serif`;
  const roleLines = includeRoles && node.roles && node.roles.length > 0
    ? wrapText(ctx, node.roles.join(', '), maxWidth, 2)
    : [];

  const blocks = [
    ...nameLines.map(text => ({
      text,
      font: `700 ${fontSize}px 'Karla Variable', Karla, sans-serif`,
      fill: isHub ? LABEL_INK : '#E8E8E8',
      height: fontSize * 1.12,
    })),
    ...roleLines.map(text => ({
      text,
      font: `500 ${roleFontSize}px 'Karla Variable', Karla, sans-serif`,
      fill: '#A0A0A0',
      height: roleFontSize * 1.25,
    })),
  ];

  let maxW = 0;
  let totalH = 0;
  blocks.forEach(block => {
    ctx.font = block.font;
    maxW = Math.max(maxW, ctx.measureText(block.text).width);
    totalH += block.height;
  });

  const startY = isHub ? originY - totalH / 2 : (stackDir > 0 ? originY : originY - totalH);
  const left = textAlign === 'center'
    ? originX - maxW / 2
    : textAlign === 'right'
      ? originX - maxW
      : originX;

  return {
    originX,
    originY: startY,
    textAlign,
    blocks,
    inverted: isHub,
    box: { x: left, y: startY, w: maxW, h: totalH },
  };
};

const ringHasLabelOverlap = (ring, ctx, scale, nodeSize, pad) => {
  const plans = ring.nodes.map(node => labelPlan(ctx, node, nodeSize, false, scale, false));
  for (let i = 0; i < plans.length; i++) {
    for (let j = i + 1; j < plans.length; j++) {
      if (boxesOverlap(plans[i].box, plans[j].box, pad)) return true;
    }
  }
  return false;
};

const resolveLabelCollisions = (rings, inner, outer, ctx, scale, nodeSize) => {
  if (!ctx) {
    rings.forEach(placeRing);
    return;
  }

  const pad = 10 / scale;

  rings.forEach((ring) => {
    placeRing(ring);
    if (ring.nodes.length < 2) return;

    for (let step = 0; step < 14; step++) {
      if (!ringHasLabelOverlap(ring, ctx, scale, nodeSize, pad)) break;
      if (ring.radius < outer - 1) {
        ring.radius = Math.min(outer, ring.radius + 8);
      } else if (ring.nodes.length > 2 && step === 8) {
        ring.offset = (ring.offset || 0) + Math.PI / ring.nodes.length / 2;
      } else {
        break;
      }
      placeRing(ring);
    }
  });

  for (let pass = 0; pass < 8; pass++) {
    let moved = false;
    const plans = [];
    rings.forEach((ring, ringIndex) => {
      ring.nodes.forEach(node => {
        plans.push({
          node,
          ringIndex,
          plan: labelPlan(ctx, node, nodeSize, false, scale, false),
        });
      });
    });

    for (let i = 0; i < plans.length; i++) {
      for (let j = i + 1; j < plans.length; j++) {
        if (!boxesOverlap(plans[i].plan.box, plans[j].plan.box, pad)) continue;
        const outerPlan = plans[i].node.__radius >= plans[j].node.__radius ? plans[i] : plans[j];
        const next = Math.min(outer, outerPlan.node.__radius + 10);
        if (next > outerPlan.node.__radius + 0.5) {
          outerPlan.node.__radius = next;
          rings[outerPlan.ringIndex].radius = Math.max(rings[outerPlan.ringIndex].radius, next);
          applyPolar(outerPlan.node);
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
};

const layoutMembershipRadial = (nodes, hubId, width, height) => {
  const { vinylR, labelR, inner, outer } = vinylGeometry(width, height);
  const hub = nodes.find(node => node.id === hubId) || nodes[0];
  if (!hub) return { rings: [], vinylR, labelR };

  hub.x = hub.y = hub.fx = hub.fy = 0;
  hub.__angle = Math.PI / 2;
  hub.__radius = 0;

  const members = nodes
    .filter(node => node !== hub)
    .sort((a, b) => (a.value || 99) - (b.value || 99));

  if (members.length === 0) return { rings: [], vinylR, labelR };

  const rings = assignRings(members, inner, outer, MIN_ARC);
  const scale = defaultScale(width, height, vinylR);
  const nodeSize = memberDotSize(vinylR);
  resolveLabelCollisions(rings, inner, outer, measureCtx, scale, nodeSize);

  return { rings: rings.map(ring => ring.radius), vinylR, labelR };
};

const paintLabelPlan = (ctx, plan) => {
  ctx.textAlign = plan.textAlign;
  ctx.textBaseline = 'top';
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.globalAlpha = 1;

  let y = plan.originY;
  plan.blocks.forEach((block, i) => {
    ctx.font = block.font;
    ctx.lineWidth = plan.inverted ? (i === 0 ? 6 : 4.5) : (i === 0 ? 4 : 3);
    ctx.strokeStyle = plan.inverted
      ? 'rgba(246, 241, 228, 0.96)'
      : 'rgba(14, 14, 14, 0.92)';
    ctx.strokeText(block.text, plan.originX, y);
    ctx.fillStyle = block.fill;
    ctx.fillText(block.text, plan.originX, y);
    y += block.height;
  });
};

const paintRecord = (ctx, vinylR, labelR, globalScale) => {
  ctx.save();

  ctx.beginPath();
  ctx.arc(0, 0, vinylR + 7 / globalScale, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.38)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, vinylR, 0, Math.PI * 2);
  ctx.fillStyle = '#161616';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, vinylR, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(246, 241, 228, 0.08)';
  ctx.lineWidth = 2 / globalScale;
  ctx.stroke();

  const grooveStart = labelR + 8;
  const grooveEnd = vinylR - 10;
  const grooves = 6;
  ctx.lineWidth = 1.1 / globalScale;
  for (let i = 0; i < grooves; i++) {
    const t = (i + 0.5) / grooves;
    const radius = grooveStart + (grooveEnd - grooveStart) * t;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(246, 241, 228, 0.07)';
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.arc(0, 0, vinylR, 0, Math.PI * 2);
  ctx.clip();
  const sheen = ctx.createRadialGradient(
    -vinylR * 0.38,
    -vinylR * 0.42,
    vinylR * 0.04,
    -vinylR * 0.1,
    -vinylR * 0.16,
    vinylR * 0.95
  );
  sheen.addColorStop(0, 'rgba(255, 255, 255, 0.07)');
  sheen.addColorStop(0.28, 'rgba(255, 255, 255, 0.02)');
  sheen.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = sheen;
  ctx.fillRect(-vinylR, -vinylR, vinylR * 2, vinylR * 2);

  ctx.restore();
  ctx.save();

  ctx.beginPath();
  ctx.arc(0, 0, labelR, 0, Math.PI * 2);
  ctx.fillStyle = PAPER;
  ctx.fill();
  ctx.strokeStyle = 'rgba(26, 18, 8, 0.14)';
  ctx.lineWidth = 1 / globalScale;
  ctx.stroke();

  ctx.restore();
};

const graphCoordsFromEvent = (fg, event) => {
  if (!fg || !event) return null;
  const canvas = event.target?.tagName === 'CANVAS'
    ? event.target
    : event.currentTarget?.querySelector?.('canvas');
  if (canvas && canvas.getBoundingClientRect) {
    const rect = canvas.getBoundingClientRect();
    return fg.screen2GraphCoords(event.clientX - rect.left, event.clientY - rect.top);
  }
  if (typeof event.offsetX === 'number') {
    return fg.screen2GraphCoords(event.offsetX, event.offsetY);
  }
  return null;
};

const Graph = ({ data, onNodeClick, width, height, isMobile, activeNodeId }) => {
  const graphRef = useRef();
  const visibleLabelsRef = useRef(new Set());
  const hoveredRef = useRef(null);
  const nodesRef = useRef([]);

  const memoizedData = useMemo(() => {
    const nodes = data.nodes.map(node => ({
      ...node,
      color: colorScale(node.value),
    }));
    const links = data.links.map(link => ({
      ...link,
      value: 1
    }));

    const layout = layoutMembershipRadial(nodes, activeNodeId, width, height);

    return {
      nodes,
      links,
      rings: layout.rings,
      vinylR: layout.vinylR,
      labelR: layout.labelR,
    };
  }, [data, width, height, activeNodeId]);

  nodesRef.current = memoizedData.nodes;

  const nodeSizeFor = useCallback((node) => {
    const { labelR, vinylR } = vinylGeometry(width, height);
    if (node.id === activeNodeId) return Math.max(10, labelR * 0.08);
    const memberCount = Math.max(1, data.nodes.length - 1);
    const memberMax = memberDotSize(vinylR);
    const memberMin = memberMax * 0.72;
    const t = memberCount <= 1 ? 0 : ((node.value || 1) - 1) / Math.max(memberCount - 1, 1);
    return memberMax - Math.min(1, Math.max(0, t)) * (memberMax - memberMin);
  }, [activeNodeId, data.nodes.length, width, height]);

  const pickNodeAt = useCallback((gx, gy) => {
    const nodes = nodesRef.current;
    let bestLabel = null;
    let bestArea = Infinity;
    nodes.forEach((node) => {
      if (!pointInBox(gx, gy, node.__hitBox)) return;
      const area = node.__hitBox.w * node.__hitBox.h;
      if (area < bestArea) {
        bestArea = area;
        bestLabel = node;
      }
    });
    if (bestLabel) return bestLabel;

    let bestDot = null;
    let bestDist = Infinity;
    nodes.forEach((node) => {
      if (node.id === activeNodeId) return;
      const radius = node.__dotR || nodeSizeFor(node);
      const dist = Math.hypot(gx - node.x, gy - node.y);
      if (dist <= radius && dist < bestDist) {
        bestDist = dist;
        bestDot = node;
      }
    });
    if (bestDot) return bestDot;

    const hub = nodes.find(node => node.id === activeNodeId);
    if (hub && Math.hypot(gx, gy) <= memoizedData.labelR) return hub;
    return null;
  }, [activeNodeId, memoizedData.labelR, nodeSizeFor]);

  const handleGraphClick = useCallback((event) => {
    const fg = graphRef.current;
    const coords = graphCoordsFromEvent(fg, event);
    if (!coords) return;
    const node = pickNodeAt(coords.x, coords.y);
    if (node && node.id) onNodeClick(node);
  }, [onNodeClick, pickNodeAt]);

  const computeVisibleLabels = useCallback((ctx, globalScale) => {
    const visible = new Set();
    const boxes = [];
    const pad = 8 / globalScale;
    const includeRoles = globalScale > 1.4;
    const hub = memoizedData.nodes.find(node => node.id === activeNodeId);
    const rest = memoizedData.nodes
      .filter(node => node !== hub)
      .sort((a, b) => (a.value || 99) - (b.value || 99));
    const ordered = hub ? [hub, ...rest] : rest;

    ordered.forEach(node => {
      const isHub = node.id === activeNodeId;
      const plan = labelPlan(
        ctx,
        node,
        nodeSizeFor(node),
        isHub,
        globalScale,
        includeRoles && !isHub
      );
      if (!plan.blocks.length) return;
      if (isHub || !boxes.some(box => boxesOverlap(box, plan.box, pad))) {
        visible.add(node.id);
        boxes.push(plan.box);
      }
    });

    return visible;
  }, [memoizedData.nodes, activeNodeId, nodeSizeFor]);

  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const isHub = node.id === activeNodeId;
    const nodeSize = nodeSizeFor(node);
    const hoveredId = hoveredRef.current && hoveredRef.current.id;
    const pad = 2 / globalScale;
    node.__dotR = isHub
      ? memoizedData.labelR
      : nodeSize + (isMobile ? 6 : 4) / globalScale;

    const labelled = isHub || visibleLabelsRef.current.has(node.id);
    let plan = null;
    if (labelled) {
      const includeRoles = globalScale > 1.4 && !isHub;
      plan = labelPlan(ctx, node, nodeSize, isHub, globalScale, includeRoles);
      node.__hitBox = {
        x: plan.box.x - pad,
        y: plan.box.y - pad,
        w: plan.box.w + pad * 2,
        h: plan.box.h + pad * 2,
      };
    } else {
      node.__hitBox = null;
    }

    if (!isHub) {
      const original = Boolean(node.isOriginal);
      const lit = hoveredId === node.id;
      ctx.globalAlpha = lit ? 0.96 : 0.88;
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI, false);
      ctx.fillStyle = original ? PAPER : node.color;
      ctx.fill();
      if (original) {
        ctx.globalAlpha = 1;
        ctx.strokeStyle = MEAT;
        ctx.lineWidth = 1.3 / globalScale;
        ctx.stroke();
      }
    }

    if (plan && hoveredId !== node.id) {
      paintLabelPlan(ctx, plan);
    }
  }, [activeNodeId, nodeSizeFor, isMobile, memoizedData.labelR]);

  const nodePointerAreaPaint = useCallback((node, color, ctx) => {
    ctx.fillStyle = color;
    if (node.__dotR) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.__dotR, 0, 2 * Math.PI);
      ctx.fill();
    }
    if (node.__hitBox) {
      ctx.fillRect(node.__hitBox.x, node.__hitBox.y, node.__hitBox.w, node.__hitBox.h);
    }
  }, []);

  const fitGraphToCanvas = useCallback(() => {
    if (!graphRef.current || width <= 0 || height <= 0) return;
    const fg = graphRef.current;
    const layoutR = memoizedData.vinylR || memoizedData.nodes.reduce((max, node) => {
      return Math.max(max, Math.hypot(node.x || 0, node.y || 0));
    }, 0);
    const spread = Math.min(width, height);
    const zoom = layoutR > 0
      ? (spread / 2) / (layoutR + 18)
      : 1.4;
    fg.centerAt(0, 0, 0);
    fg.zoom(Math.max(0.35, zoom), 0);
  }, [width, height, memoizedData]);

  const handleRenderFramePre = useCallback((ctx, globalScale) => {
    if (memoizedData.vinylR) {
      paintRecord(ctx, memoizedData.vinylR, memoizedData.labelR, globalScale);
    }
    visibleLabelsRef.current = computeVisibleLabels(ctx, globalScale);
  }, [computeVisibleLabels, memoizedData.vinylR, memoizedData.labelR]);

  const handleRenderFramePost = useCallback((ctx, globalScale) => {
    const hovered = hoveredRef.current;
    if (!hovered || hovered.id === activeNodeId) return;
    const node = memoizedData.nodes.find(n => n.id === hovered.id);
    if (!node) return;
    const plan = labelPlan(
      ctx,
      node,
      nodeSizeFor(node),
      false,
      globalScale,
      globalScale > 1.4
    );
    paintLabelPlan(ctx, plan);
  }, [memoizedData.nodes, activeNodeId, nodeSizeFor]);

  useEffect(() => {
    const fg = graphRef.current;
    if (!fg || width <= 0 || height <= 0) return;

    fg.d3Force('charge', null);
    fg.d3Force('center', null);
    fg.d3Force('collide', null);
    const linkForce = fg.d3Force('link');
    if (linkForce) {
      linkForce.strength(0);
      linkForce.distance(0);
    }

    fitGraphToCanvas();
    const frame = requestAnimationFrame(fitGraphToCanvas);
    const timer = setTimeout(fitGraphToCanvas, 50);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [memoizedData, width, height, fitGraphToCanvas]);

  const linkColor = useCallback((link) => {
    const source = typeof link.source === 'object' ? link.source : null;
    const target = typeof link.target === 'object' ? link.target : null;
    const member = source && source.id === activeNodeId ? target : source;
    if (member && member.isOriginal) return 'rgba(196, 69, 45, 0.2)';
    return 'rgba(246, 241, 228, 0.07)';
  }, [activeNodeId]);

  return (
    <ForceGraph2D
      ref={graphRef}
      graphData={memoizedData}
      nodeLabel={() => ''}
      nodeColor="color"
      linkColor={linkColor}
      linkWidth={0.9}
      backgroundColor={INK}
      width={width}
      height={height}
      onNodeClick={(_node, event) => handleGraphClick(event)}
      onBackgroundClick={handleGraphClick}
      onNodeHover={node => { hoveredRef.current = node || null; }}
      nodeCanvasObject={nodeCanvasObject}
      nodePointerAreaPaint={nodePointerAreaPaint}
      onRenderFramePre={handleRenderFramePre}
      onRenderFramePost={handleRenderFramePost}
      warmupTicks={1}
      cooldownTicks={1}
      minZoom={0.35}
      maxZoom={12}
      autoPauseRedraw={false}
      linkDirectionalParticles={1}
      linkDirectionalParticleColor={() => 'rgba(246, 241, 228, 0.35)'}
      linkDirectionalParticleWidth={1.15}
      linkDirectionalParticleSpeed={0.0028}
      nodeRelSize={1}
      enableZoomInteraction={true}
      enablePanInteraction={true}
      enableNodeDrag={false}
      onEngineStop={fitGraphToCanvas}
    />
  );
};

export default React.memo(Graph);
